import { useEffect, useState } from "react";
import { Check, X, Loader2, ShieldCheck, Clock, CircleCheck, CircleX, Ban } from "lucide-react";

const RESOLVED_STATES = {
  executed: { icon: CircleCheck, label: "Eseguita" },
  failed: { icon: CircleX, label: "Non riuscita" },
  cancelled: { icon: Ban, label: "Annullata" },
  expired: { icon: Clock, label: "Scaduta" },
};

function useIsExpired(expiresAt, active) {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!expiresAt || !active) return undefined;
    const target = new Date(expiresAt).getTime();
    const check = () => setExpired(Date.now() >= target);
    check();
    const timer = setInterval(check, 10000);
    return () => clearInterval(timer);
  }, [expiresAt, active]);
  return expired;
}

/**
 * Scheda di conferma per un'azione proposta dall'assistente.
 * status: pending | confirming | cancelling | executed | failed | cancelled
 */
export default function ActionConfirmCard({
  action,
  status = "pending",
  outcomeMessage,
  onConfirm,
  onCancel,
  tone = "light",
}) {
  const busy = status === "confirming" || status === "cancelling";
  const expired = useIsExpired(action?.expires_at, status === "pending");
  const effectiveStatus = status === "pending" && expired ? "expired" : status;
  const resolved = RESOLVED_STATES[effectiveStatus];
  const dark = tone === "dark";
  const details = Array.isArray(action?.details) ? action.details : [];

  return (
    <div
      className={`rounded-2xl border text-sm ${
        dark
          ? "border-white/15 bg-white/10 text-white backdrop-blur-md"
          : "border-amber-200 bg-amber-50/70 text-slate-800"
      }`}
      data-testid="assistant-action-card"
    >
      <div className="px-3.5 pt-3 pb-2">
        <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${
          dark ? "text-white/70" : "text-amber-700"
        }`}>
          <ShieldCheck className="w-3.5 h-3.5" />
          {resolved ? "Azione" : "Conferma richiesta"}
          {action?.label && <span className="normal-case font-medium tracking-normal">· {action.label}</span>}
        </div>
        <p className={`mt-1 font-medium leading-snug ${dark ? "text-white" : "text-slate-900"}`}>
          {action?.riepilogo || "Operazione da confermare"}
        </p>
        {details.length > 0 && (
          <dl className={`mt-2 space-y-1 text-xs ${dark ? "text-white/80" : "text-slate-600"}`}>
            {details.slice(0, 8).map((d, idx) => (
              <div key={`${d.label}-${idx}`} className="flex gap-2">
                <dt className={`flex-shrink-0 ${dark ? "text-white/55" : "text-slate-500"}`}>{d.label}:</dt>
                <dd className="min-w-0 break-words">{String(d.value)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {resolved ? (
        <div className={`flex items-start gap-1.5 px-3.5 py-2.5 border-t text-xs ${
          dark ? "border-white/10 text-white/80" : "border-amber-200/70 text-slate-600"
        }`}>
          <resolved.icon className="w-3.5 h-3.5 mt-px flex-shrink-0" />
          <span>
            <span className="font-semibold">{resolved.label}</span>
            {effectiveStatus === "expired"
              ? " · chiedi di nuovo all'assistente se vuoi procedere."
              : outcomeMessage ? ` · ${outcomeMessage}` : ""}
          </span>
        </div>
      ) : (
        <div className={`flex gap-2 px-3.5 pb-3 pt-1`}>
          <button
            type="button"
            onClick={() => onConfirm?.(action)}
            disabled={busy}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${
              dark ? "bg-white text-slate-900 hover:bg-white/90" : "bg-emerald-600 text-white hover:bg-emerald-700"
            }`}
            data-testid="assistant-action-confirm"
          >
            {status === "confirming" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Conferma
          </button>
          <button
            type="button"
            onClick={() => onCancel?.(action)}
            disabled={busy}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${
              dark
                ? "border border-white/25 text-white hover:bg-white/10"
                : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
            data-testid="assistant-action-cancel"
          >
            {status === "cancelling" ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            Annulla
          </button>
        </div>
      )}
    </div>
  );
}
